import { ScrollView, View } from "@-/com.npmjs.react-native";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { Icon } from "@-/dev.improve.haniverse.components.ui.icon";
import { READY_MATERIAL_TAB_VALUE } from "@-/dev.improve.haniverse.lib.level";
import { useIsOnboardingTour } from "@-/dev.improve.haniverse.lib.onboarding";
import { getLevelsPackDescription } from "@-/dev.improve.haniverse.routes.nav.app.browse.tabs.levels._components.levels-list";
import { PinyinSoundCharts } from "@-/dev.improve.haniverse.routes.nav.app.browse.tabs.levels._components.pinyin-sound-charts";
import { BrowseLevelsLayoutContext } from "@-/dev.improve.haniverse.routes.nav.app.browse.tabs.levels._context";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { LevelsPackDescriptionSurface } from "#levels-pack-description-surface";
import { LevelsPackProgressSurface } from "#levels-pack-progress-surface";

/** Leave at least 640px for the catalog beside the 320px details sidebar. */
const DOCKED_MIN_WIDTH = 960;

/** Web's pack inspector shares the native sheet's expandable content below the site header. */
export function LevelsPackSheetLayout({
  children,
  renderTabs,
  tabsClassName,
  toolbar,
}: {
  children: ReactNode;
  renderTabs: (sheet: { openSheet: () => void; sheetGrabber: ReactNode }) => ReactNode;
  tabsClassName?: string;
  toolbar?: ReactNode;
}) {
  const [
    hanziScript,
    curriculumGrade,
    hasDetails,
    levels,
    dataset,
    gradeParts,
    requestLevelScroll,
  ] = useContextSelectors(
    BrowseLevelsLayoutContext,
    (c) => c.browsedHanziScript,
    (c) => c.curriculumGrade,
    (c) => c.hasPackSheet,
    (c) => c.characterLevels,
    (c) => c.curriculumDataset,
    (c) => c.gradeParts,
    (c) => c.requestLevelScroll,
  );
  const description = getLevelsPackDescription({
    dataset,
    gradeParts,
    levels,
    tabValue: curriculumGrade,
  });
  const isOnboardingTour = useIsOnboardingTour();
  const [availableWidth, setAvailableWidth] = useState(0);
  const [openPreference, setOpenPreference] = useState<boolean | null>(null);
  const isDocked = availableWidth >= DOCKED_MIN_WIDTH;
  const isOpen = hasDetails && (openPreference ?? (isDocked && !isOnboardingTour));
  const isDrawer = isOpen && !isDocked;
  const panelRef = useRef<HTMLElement>(null);
  const scrollRef = useRef<ScrollView>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ animated: false, y: 0 });
  }, [curriculumGrade]);

  // A drawer owns keyboard focus while open, then returns it to its opener.
  useEffect(() => {
    if (!isDrawer) {
      return;
    }
    const previousFocus = document.activeElement;
    const panel = panelRef.current;
    panel?.querySelector<HTMLButtonElement>("button")?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setOpenPreference(false);
      }
      if (panel === null || event.key !== "Tab") {
        return;
      }
      const controls = [
        ...panel.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        ),
      ].filter((element) => element.getClientRects().length > 0 && !element.matches(":disabled"));
      const first = controls[0];
      const last = controls.at(-1);
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      if (
        previousFocus instanceof HTMLElement &&
        previousFocus !== document.body &&
        previousFocus.isConnected
      ) {
        previousFocus.focus();
      } else {
        document.querySelector<HTMLButtonElement>("#levels-pack-details-toggle")?.focus();
      }
    };
  }, [isDrawer]);

  return (
    <View
      className="relative min-h-0 flex-1 flex-row overflow-hidden"
      onLayout={(event) => {
        const width = event.nativeEvent.layout.width;
        setAvailableWidth(width);
        if (width > 0) {
          setOpenPreference(
            (preference) => preference ?? (width >= DOCKED_MIN_WIDTH && !isOnboardingTour),
          );
        }
      }}
      testID="levels-pack-workspace"
    >
      <div className="flex min-h-0 min-w-0 flex-1 flex-col" inert={isDrawer}>
        {toolbar}
        <View className="flex-row items-center border-b border-border">
          <View className={`min-w-0 flex-1 ${tabsClassName ?? ""}`}>
            {renderTabs({ openSheet: () => setOpenPreference(true), sheetGrabber: undefined })}
          </View>
          {hasDetails && !isOpen ? (
            <button
              aria-controls="levels-pack-details"
              aria-expanded={false}
              aria-label="Show pack details"
              className="m-2 flex size-8 shrink-0 items-center justify-center rounded-xs text-muted-foreground hover:bg-accent active:bg-accent"
              data-testid="levels-pack-sidebar-toggle"
              id="levels-pack-details-toggle"
              onClick={() => setOpenPreference(true)}
              type="button"
            >
              <Icon name="TbLayoutSidebarRight" size={20} />
            </button>
          ) : null}
        </View>
        <View className="min-h-0 flex-1">{children}</View>
      </div>
      {isDrawer ? (
        <button
          aria-label="Close pack details"
          className="absolute inset-0 z-10 bg-black/30"
          data-testid="levels-pack-drawer-backdrop"
          onClick={() => setOpenPreference(false)}
          tabIndex={-1}
          type="button"
        />
      ) : null}
      {isOpen ? (
        <aside
          aria-label="Pack details"
          aria-modal={isDrawer ? true : undefined}
          className={`flex min-h-0 w-80 max-w-full shrink-0 flex-col border-l border-border bg-background ${isDocked ? "" : "absolute inset-y-0 right-0 z-20 shadow-xl"}`}
          data-testid="levels-pack-sidebar"
          id="levels-pack-details"
          ref={panelRef}
          role={isDrawer ? "dialog" : undefined}
        >
          <div className="flex shrink-0 items-center justify-between gap-2 px-3 pt-2.25 pb-2">
            <span className="text-sm font-semibold text-foreground">Pack details</span>
            <button
              aria-controls="levels-pack-details"
              aria-expanded
              aria-label="Hide pack details"
              className="flex size-8 items-center justify-center rounded-xs text-muted-foreground hover:bg-accent active:bg-accent"
              data-testid="levels-pack-sidebar-close"
              onClick={() => setOpenPreference(false)}
              type="button"
            >
              <Icon name="TbLayoutSidebarRightFilled" size={20} />
            </button>
          </div>
          <ScrollView ref={scrollRef} testID="levels-pack-sidebar-scroll">
            <View className="gap-1 p-2 pt-0">
              <LevelsPackDescriptionSurface
                description={description}
                title={description?.title ?? "Pack"}
              />
              {curriculumGrade === READY_MATERIAL_TAB_VALUE ? (
                <PinyinSoundCharts
                  hanziScript={hanziScript}
                  onShowLevel={(introduction) => {
                    requestLevelScroll({
                      levelIndex: introduction.levelIndex,
                      part: introduction.levelPart,
                    });
                    if (!isDocked) {
                      setOpenPreference(false);
                    }
                  }}
                />
              ) : (
                <LevelsPackProgressSurface />
              )}
            </View>
          </ScrollView>
        </aside>
      ) : null}
    </View>
  );
}
